/**
 * Product-page "Add to quote" button. Reads the currently selected variant
 * from the surrounding product-form, adds it to the global QuoteCart, and
 * fires a `quote-cart:added` event so the page-level toast can show.
 */
if (!customElements.get('quote-request')) {
  customElements.define(
    'quote-request',
    class QuoteRequest extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.addButton = this.querySelector('[data-quote-add]');
        this.addLabel = this.querySelector('[data-quote-add-label]');
        this.defaultLabel = this.addLabel?.textContent?.trim() || 'Add to quote';

        this.addButton?.addEventListener('click', this.add.bind(this));
        this.variantChangeUnsubscriber = this.subscribeToVariantChanges();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return null;
        return subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== this.dataset.sectionId) return;
          this.currentVariant = data.variant;
          if (this.addButton) {
            this.addButton.disabled = !data.variant?.id || !data.variant?.sku;
          }
        });
      }

      add() {
        const variantId = this.variantId;
        const quantity = this.quantity;
        if (!variantId || quantity <= 0) return;
        if (!window.QuoteCart) return;

        // Shopify's variant.price is in cents (integer). The Liquid data
        // attribute falls back to the initial variant's price (also in
        // cents — Liquid `variant.price` is the raw money amount in the
        // shop's currency subunits). We store cents so totals on the
        // quote-page can do integer math and avoid floating drift.
        const priceCents = Number.parseInt(
          this.currentVariant?.price ?? this.dataset.variantPrice ?? '',
          10,
        );
        const item = {
          variant_id: String(variantId),
          sku: this.currentVariant?.sku || this.dataset.variantSku || this.currentSku,
          product_title: this.dataset.productTitle || '',
          variant_title: this.currentVariant?.title || this.dataset.variantTitle || '',
          options: this.selectedOptions(),
          quantity,
          price_cents: Number.isFinite(priceCents) ? priceCents : null,
          image_url: this.currentImage(),
          product_url: this.dataset.productUrl || '',
        };

        const result = window.QuoteCart.add(item);
        if (!result) {
          // QuoteCart returns null when the cart is full or the variant_id
          // / quantity didn't validate. Surface the cap so the user knows
          // why the click looked like a no-op.
          this.flashLabel(`Quote is full (${window.QuoteCart.MAX_ITEMS} items max)`);
          return;
        }

        this.flashLabel('Added!');
        window.dispatchEvent(
          new CustomEvent('quote-cart:added', { detail: { item: result } })
        );
      }

      flashLabel(message) {
        if (!this.addLabel) return;
        this.addLabel.textContent = message;
        clearTimeout(this._flashTimer);
        this._flashTimer = setTimeout(() => {
          this.addLabel.textContent = this.defaultLabel;
        }, 1600);
      }

      get variantId() {
        return this.productInfo?.querySelector(
          `#product-form-${this.dataset.sectionId} input[name="id"]`
        )?.value;
      }

      get quantityInput() {
        return this.productInfo?.querySelector(`#Quantity-${this.dataset.sectionId}`);
      }

      get quantity() {
        const value = Number.parseFloat(this.quantityInput?.value || '1');
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      get currentSku() {
        // The SKU <p> contains a visually-hidden "SKU:" label, so the raw
        // textContent is something like "\n  SKU:SL60-3-P-F-L". Collapse
        // whitespace and trim before stripping the prefix — otherwise the
        // leading space prevents the ^sku: regex from matching.
        const skuText =
          this.productInfo?.querySelector(`#Sku-${this.dataset.sectionId}`)?.textContent || '';
        return skuText.replace(/\s+/g, ' ').trim().replace(/^sku:\s*/i, '') || null;
      }

      currentImage() {
        return (
          this.currentVariant?.featured_image?.src ||
          this.currentVariant?.featured_media?.preview_image?.src ||
          this.dataset.productImage ||
          null
        );
      }

      selectedOptions() {
        const variantTitle = this.currentVariant?.title || this.dataset.variantTitle || '';
        if (!variantTitle || variantTitle === 'Default Title') return [];

        const productInfo = this.productInfo;
        if (!productInfo) {
          return variantTitle.split(' / ').map((value, index) => ({
            name: `Option ${index + 1}`,
            value,
          }));
        }

        const rows = Array.from(productInfo.querySelectorAll('.product-form__input')).flatMap(
          (group) => {
            const label = group.querySelector('legend, .form__label')?.textContent || '';
            const name = label.replace(/\s+/g, ' ').replace(/:.*$/, '').trim();
            if (!name) return [];

            const selected =
              group.querySelector('input[type="radio"]:checked') ||
              group.querySelector('select');
            const value =
              selected?.tagName === 'SELECT'
                ? selected.selectedOptions?.[0]?.textContent
                : selected?.value;
            const cleaned = String(value || '').trim();
            return cleaned ? [{ name, value: cleaned }] : [];
          }
        );

        if (rows.length > 0) return rows;
        return variantTitle.split(' / ').map((value, index) => ({
          name: `Option ${index + 1}`,
          value,
        }));
      }
    }
  );
}
